/**
 * session-boot — boot-time session triage (pure, no imports).
 *
 * Answers ONE question for App.jsx's `user` useState initializer: can the
 * token pair sitting in sessionStorage still turn into an authenticated
 * session without the operator re-entering credentials?
 *
 *   - Access token valid (by its own exp claim)      → yes, boot logged-in.
 *   - Access token expired BUT a refresh token exists → yes: apiFetch's
 *     proactive-refresh path (data-service-api.js) silently rotates it on
 *     the first request.
 *   - Access token expired/garbage and NO refresh token → no. Nothing on a
 *     prod build can mint a token without the login form (devAutoLogin has
 *     no VITE_DEV_AUTH_* credentials there), so booting "logged-in" produces
 *     a half-alive app: every API call 401s ("Authentication required"),
 *     the session-resume load fails with an error toast, and — before the
 *     v5.50.7 fix — the resume's catch DELETED the saved-project receipt,
 *     so the open project was forgotten even after the operator signed back
 *     in. The truthful boot for this state is the login page.
 *
 * Expiry semantics deliberately mirror isTokenExpired() in
 * data-service-api.js: a missing/malformed exp claim or an unparseable token
 * counts as expired, and the same 30 s safety margin applies so a token
 * about to lapse mid-boot is not trusted.
 */

export interface BootTokenClaims {
    userId?: string;
    tenantId?: string;
    role?: string;
    mustChangePassword?: boolean;
    exp?: number;
    [key: string]: unknown;
}

export type PasswordRotationBootState = 'required' | 'clear' | 'checking' | 'failed';

/**
 * Resolve the signed password-rotation claim without treating absence as false.
 *
 * New tokens carry an explicit boolean. Tokens issued by an older backend during
 * a rolling deployment do not, so they must wait for authoritative `/auth/me`
 * hydration before the application is allowed to render.
 */
export function passwordRotationStateFromClaims(
    claims: Pick<BootTokenClaims, 'mustChangePassword'> | null | undefined,
): PasswordRotationBootState {
    if (claims?.mustChangePassword === true) return 'required';
    if (claims?.mustChangePassword === false) return 'clear';
    return 'checking';
}

/** Resolve the authoritative `/auth/me` answer after legacy-token hydration. */
export function passwordRotationStateFromIdentity(
    identity: { mustChangePassword?: unknown } | null | undefined,
): PasswordRotationBootState {
    if (identity?.mustChangePassword === true) return 'required';
    if (identity?.mustChangePassword === false) return 'clear';
    return 'failed';
}

/** Only an explicit signed or hydrated `false` may enter the application. */
export function canRenderAuthenticatedApp(state: PasswordRotationBootState): boolean {
    return state === 'clear';
}

/** Decode a JWT payload without verifying the signature (display-gating only). */
export function decodeJwtClaims(token: string | null | undefined): BootTokenClaims | null {
    if (!token || typeof token !== 'string') return null;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = String(parts[1]).replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
        const decoded: unknown = JSON.parse(atob(padded));
        return decoded && typeof decoded === 'object' ? (decoded as BootTokenClaims) : null;
    } catch {
        return null;
    }
}

const EXPIRY_MARGIN_SECONDS = 30; // matches isTokenExpired() in data-service-api.js

/** True when the token is missing, unparseable, or past exp (30 s margin). */
export function isAccessTokenExpired(token: string | null | undefined, nowMs: number = Date.now()): boolean {
    const claims = decodeJwtClaims(token);
    if (!claims || typeof claims.exp !== 'number') return true;
    return claims.exp < Math.floor(nowMs / 1000) + EXPIRY_MARGIN_SECONDS;
}

/**
 * True when the stored token pair can still become an authenticated session
 * without the login form. False means: boot to the login page (and leave the
 * qm_last_view_state receipt alone — LoginPage hard-reloads after sign-in and
 * the session-resume then restores the open project with fresh auth).
 */
export function isSessionRecoverable(
    accessToken: string | null | undefined,
    refreshToken: string | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    if (!accessToken) return false;
    if (!isAccessTokenExpired(accessToken, nowMs)) return true;
    return typeof refreshToken === 'string' && refreshToken.length > 0;
}
